import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useVacationHistoryReport } from '../../../hooks/useVacationHistoryReport';
import { VacationHistoryResultsCards } from '../../../components/VacationHistoryResultsCards';
import { VacationRequestDetailsModal } from '../../../components/VacationRequestDetailsModal';
import { buildHistoryCsvRows } from '../../../utils/vacationHistoryReport';
import { downloadCsv } from '@/utils/csvExport';
import { TourDemoHistoryResults, HISTORY_DEMO_KIND } from './tourDemo/TourDemoHistoryResults';
import {
    SITUATION_LABEL,
    SITUATION_OPTIONS,
    MONTH_LABEL,
    YEAR_LABEL,
    ALL_MONTHS,
    SEARCH_PLACEHOLDER,
    SEARCH_LABEL,
    DOWNLOAD_BUTTON_LABEL,
    RESULT_LABEL_TEMPLATE,
    EMPTY_STATE_MESSAGE,
    MONTH_NAMES,
} from './HistoryTab.definitions';
import type { HistoryEventKind } from '../../../models/vacationHistoryReport.types';

const DEMO_RESULT_COUNT = 3;

interface HistoryTabProps {
    isTourDemo?: boolean;
}

export function HistoryTab({ isTourDemo = false }: HistoryTabProps) {
    const {
        rows,
        counts,
        yearOptions,
        isLoading,
        kind,
        setKind,
        selectedMonth,
        setSelectedMonth,
        selectedYear,
        setSelectedYear,
        search,
        setSearch,
        periodFilterKey,
        findRequest,
        findVacationRequest,
    } = useVacationHistoryReport();

    const { user } = useAuth();
    const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);

    const selectedRow = selectedRequestId ? findRequest(selectedRequestId) : null;
    const selectedRequest = selectedRequestId ? findVacationRequest(selectedRequestId) : null;

    const now = new Date();
    const shownKind: HistoryEventKind = isTourDemo ? HISTORY_DEMO_KIND : kind;
    const shownMonth = isTourDemo ? now.getMonth() + 1 : selectedMonth;
    const shownYear = isTourDemo ? now.getFullYear() : selectedYear;
    const shownSearch = isTourDemo ? '' : search;
    const shownTotal = isTourDemo ? DEMO_RESULT_COUNT : counts.total;
    const shownPeople = isTourDemo ? DEMO_RESULT_COUNT : counts.people;

    const getKindLabel = () => SITUATION_OPTIONS.find((o) => o.value === shownKind)?.label || shownKind;
    const getPeriodLabel = () => {
        if (shownMonth) {
            return `${MONTH_NAMES[shownMonth - 1].toLowerCase()}/${shownYear}`;
        }
        return String(shownYear);
    };

    const handleDownload = () => {
        const { headers, rows: csvRows } = buildHistoryCsvRows(rows, { kind, periodKey: periodFilterKey });
        const kindLabel = getKindLabel();
        const period = getPeriodLabel();
        const filename = `historico-ferias-${kindLabel.toLowerCase().replace(/\s+/g, '-')}-${period.replace('/', '-')}.csv`;
        downloadCsv(filename, headers, csvRows);
    };

    const showResults = isTourDemo || rows.length > 0;

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-4" data-tour="history-filters">
                <div data-tour="history-situation">
                    <label htmlFor="situation" className="block text-sm font-medium text-gray-700 mb-1">
                        {SITUATION_LABEL}
                    </label>
                    <select
                        id="situation"
                        value={shownKind}
                        onChange={(e) => setKind(e.target.value as HistoryEventKind)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500"
                    >
                        {SITUATION_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="grid grid-cols-2 gap-4 sm:col-span-2" data-tour="history-period">
                    <div>
                        <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-1">
                            {YEAR_LABEL}
                        </label>
                        <select
                            id="year"
                            value={shownYear ?? ''}
                            onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value, 10) : null)}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500"
                        >
                            {yearOptions.map((year) => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label htmlFor="month" className="block text-sm font-medium text-gray-700 mb-1">
                            {MONTH_LABEL}
                        </label>
                        <select
                            id="month"
                            value={shownMonth ?? ''}
                            onChange={(e) => setSelectedMonth(e.target.value ? parseInt(e.target.value, 10) : null)}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500"
                        >
                            <option value="">{ALL_MONTHS}</option>
                            {MONTH_NAMES.map((name, idx) => (
                                <option key={idx} value={idx + 1}>
                                    {name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div data-tour="history-search">
                    <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                        {SEARCH_LABEL}
                    </label>
                    <input
                        id="search"
                        type="text"
                        placeholder={SEARCH_PLACEHOLDER}
                        value={shownSearch}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:ring-primary-500"
                    />
                </div>
            </div>

            {showResults && (
                <div className="flex flex-col gap-4" data-tour="history-results">
                    <div className="flex items-center justify-between" data-tour="history-summary">
                        <p className="text-sm font-medium text-gray-700">
                            {RESULT_LABEL_TEMPLATE(getKindLabel(), getPeriodLabel(), shownTotal, shownPeople)}
                        </p>
                        <button
                            onClick={handleDownload}
                            disabled={rows.length === 0}
                            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                            data-tour="history-download"
                        >
                            {DOWNLOAD_BUTTON_LABEL}
                        </button>
                    </div>

                    {isTourDemo ? (
                        <TourDemoHistoryResults />
                    ) : (
                        <VacationHistoryResultsCards
                            rows={rows}
                            isLoading={isLoading}
                            onSelectRequest={(row: any) => setSelectedRequestId(row.requestId)}
                            highlightedKind={kind}
                            kind={kind}
                            periodFilterKey={periodFilterKey}
                        />
                    )}
                </div>
            )}

            {!showResults && !isLoading && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
                    <p className="text-sm text-gray-600">{EMPTY_STATE_MESSAGE}</p>
                </div>
            )}

            {selectedRequest && selectedRow && (
                <VacationRequestDetailsModal
                    request={selectedRequest}
                    onClose={() => setSelectedRequestId(null)}
                    currentUserEmail={user?.email ?? ''}
                    collaboratorName={selectedRow.collaborator.name}
                    periodAllocations={selectedRow.periodAllocations}
                />
            )}
        </div>
    );
}
